import { Injectable, inject } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { AuthService } from '../../../../auth/auth.service';
import { TimerStatus } from '../../../../features/calendar/calendar-types';
import { SharedActiveTimeBlockService } from '../../../../features/calendar/components/time-block/active/shared-active-time-block.service';
import { ITimeBlockComponentItem } from '../../../../features/calendar/components/time-block/types/time-block-component-item-types';
import { TimerResponse } from '../../../../shared/data-types/shared-time-block-types';
import { ProjectsHttpService } from '../../../state/projects/projects-http.service';
import { TimeBlockHttpService } from '../../../state/time-blocks/time-block-entries/time-block-http.service';
import { TimerHttpService } from '../../../state/time-blocks/timer/timer-http.service';
import * as fromTimerActions from '../../../state/time-blocks/timer/timer.actions';
import { CronJobsService } from '../../cron-jobs.service';

@Injectable({
  providedIn: 'root',
})
export class SharedTimerHandlerService {
  public enlargeActiveTimeBlockJob: string | null = null;
  public timerActiveTBChanged$ = new BehaviorSubject<number | null>(null);
  public timerPosted$ = new Subject<TimerResponse>();

  // Timer data
  public timerStatus: TimerStatus = TimerStatus.Stop;

  // DI
  private readonly cronJobsService = inject(CronJobsService);
  private readonly projectsHttpService = inject(ProjectsHttpService);
  private readonly timerHttpService = inject(TimerHttpService);
  private readonly timeBlockHttpService = inject(TimeBlockHttpService);
  private readonly authService = inject(AuthService);
  private readonly actions$ = inject(Actions);

  constructor() {
    this.actions$
      .pipe(
        ofType(fromTimerActions.timerPosted),
        map((action) => action.payload),
      )
      .subscribe((payload) => {
        this.timerPosted$.next(payload);
        const timeBlockId =
          payload.status === TimerStatus.Start
            ? payload.generatedTimeBlock.id
            : SharedActiveTimeBlockService.noActiveTimerTimeBlockId;
        this.timerActiveTBChanged$.next(timeBlockId);
      });
  }

  public registerTimeTracking(initTimeTracking: () => void): void {
    initTimeTracking();
  }

  public deregisterTimeTracking(): void {
    if (this.enlargeActiveTimeBlockJob) {
      this.cronJobsService.deregisterJob(this.enlargeActiveTimeBlockJob);
      this.enlargeActiveTimeBlockJob = null;
    }
  }

  public toggleTimer(buttonDisabled?: boolean): void {
    if (buttonDisabled) {
      return;
    }

    const activeTimeBlockId = this.timerActiveTBChanged$.value;
    if (Number.isFinite(activeTimeBlockId) && activeTimeBlockId >= 0) {
      this.timerStatus = TimerStatus.Stop;
      this.sendTimerRequest();
      return;
    }

    this.timerStatus = TimerStatus.Start;

    // Send request
    this.sendTimerRequest();
  }

  public activeTimerTimeBlockExists(): Observable<ITimeBlockComponentItem> {
    const loggedInUserId = this.authService.getLoggedInUserClone().id;
    return this.timeBlockHttpService.fetchActiveTimerTimeBlock(loggedInUserId);
  }

  private sendTimerRequest(): void {
    // Timer is already running. No need to start it again.
    const activeTimeBlockId = this.timerActiveTBChanged$.value;
    if (
      Number.isFinite(activeTimeBlockId) &&
      activeTimeBlockId >= 0 &&
      this.timerStatus === TimerStatus.Start
    ) {
      return;
    }

    // todo at the moment we use an arbitrary task from an arbitrary project to track time
    // use the taskId from the last tracked task
    this.projectsHttpService
      .getProjects()
      .pipe(take(1))
      .subscribe((projects) => {
        this.timerHttpService.postTimer(this.timerStatus, projects[0].tasks[0].id);
      });
  }
}
